import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { ConfirmDialogService } from '../../dialog/confirm-dialog/confirm-dialog.service';
import { BookingGroupService } from './bookinggroup.service';

/** Dependencies required to run the shared booking group delete flow. */
export interface DeleteBookingGroupOptions {
  /** Id of the booking group to delete. */
  id: string;
  /** Name of the booking group, shown in the blocking dialog. */
  bookingGroupName: string;
  service: BookingGroupService;
  dialogService: ConfirmDialogService;
  destroyRef: DestroyRef;
  /** Called after the booking group has been removed successfully. */
  onDeleted: () => void;
}

/**
 * Runs the shared "Verwijderen" flow for a booking group:
 * checks for booking definitions that still use it and either shows a blocking
 * dialog or asks for confirmation before deleting.
 */
export function deleteBookingGroupWithChecks(options: DeleteBookingGroupOptions): void {
  const { id, service, dialogService, destroyRef } = options;

  // A booking group cannot be removed while booking definitions still use it.
  service
    .getBookingDefinitionsByGroup(id)
    .pipe(takeUntilDestroyed(destroyRef))
    .subscribe({
      next: (definitions) => {
        if (definitions && definitions.length > 0) {
          showDeleteNotPossibleDialog(dialogService, options.bookingGroupName);
        } else {
          confirmAndDeleteBookingGroup(options);
        }
      },
      error: () => confirmAndDeleteBookingGroup(options),
    });
}

function showDeleteNotPossibleDialog(
  dialogService: ConfirmDialogService,
  bookingGroupName: string,
): void {
  dialogService.open({
    width: '400px',
    title: 'Verwijderen niet mogelijk',
    message: `Boekingsgroep '${bookingGroupName}' kan niet worden verwijderd omdat er nog boekingsdefinities aan gekoppeld zijn.`,
    confirmText: 'OK',
    variant: 'warning',
  });
}

function confirmAndDeleteBookingGroup(options: DeleteBookingGroupOptions): void {
  const { id, service, dialogService, destroyRef, onDeleted } = options;

  dialogService.open({
    width: '320px',
    title: 'Verwijderen?',
    message: 'Boekingsgroep verwijderen?',
    cancelText: 'Nee',
    confirmText: 'Ja',
    variant: 'error',
  });

  dialogService
    .confirmed()
    .pipe(
      take(1),
      switchMap((confirmed) => {
        if (!confirmed) {
          return EMPTY;
        }

        return service.removeBookingGroup(id);
      }),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe({
      next: () => onDeleted(),
      error: () => undefined,
    });
}
